/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from "react";
import { Camera, Eye, MapPin, Feather, Compass, ArrowRight, Heart, Star } from "lucide-react";

interface AboutProps {
  onJoinWaitlist: () => void;
}

export default function About({ onJoinWaitlist }: AboutProps) {
  return (
    <section className="bg-[#F8F5EF] text-[#22201D] py-16 sm:py-24 px-4 sm:px-6 relative overflow-hidden" id="about-section">
      <div className="max-w-5xl mx-auto">
        
        {/* Editorial Subheader */}
        <div className="text-center mb-16 relative">
          <span className="text-[10px] tracking-[0.35em] uppercase text-[#4A1722] font-display flex items-center justify-center gap-1.5 mb-4 font-bold">
            <Compass size={11} /> The Atelier Story & Philosophy
          </span>
          <h2 className="font-serif text-[42px] sm:text-[58px] font-extralight tracking-tight leading-tight text-[#241018]">
            A boutique studio focused on photos that last forever
          </h2>
          <p className="font-serif font-light text-[11px] uppercase tracking-[0.25em] text-[#4A1722] mt-3">
            True Connection • Exceptional Quality • Sincere Artistry
          </p>
          <div className="w-20 h-[1px] bg-[#4A1722]/45 mx-auto my-6"></div>
        </div>

        {/* Column Grid layout split */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16 items-center">
          
          {/* Left Column - Large Editorial Portrait Teaser */}
          <div className="md:col-span-5 space-y-4">
            <div className="border border-[#1A1A1A] p-3 bg-white">
              <div className="overflow-hidden aspect-[4/5] bg-secondary relative">
                <img
                  src="/rose/demo/founders-portrait-real-look.png"
                  alt="Creative Director at work"
                  referrerPolicy="no-referrer"
                  className="w-full h-full object-cover grayscale transition duration-1000 hover:grayscale-0"
                />
              </div>
              <div className="pt-3 text-center">
                <p className="font-serif text-sm italic font-medium">Charlotte Rose & Julian Sterling</p>
                <p className="text-[9px] uppercase tracking-widest text-neutral-400 mt-0.5">Atelier Founders</p>
              </div>
            </div>
            

          </div>

          {/* Right Column - Narrative text */}
          <div className="md:col-span-7 space-y-6">
            <h3 className="font-serif text-[26px] sm:text-[32px] font-extralight tracking-tight text-[#4A1722] leading-tight font-semibold">
              Timeless Imagery Created With Intention
            </h3>
            
            <div className="font-serif text-[15px] sm:text-[16px] text-[#22201D] leading-relaxed space-y-4 font-light">
              <p>
                The Rose Photo was created for couples who believe their wedding photographs should be more than a record of the day. They should become part of a family's history.
              </p>
              <p>
                By accepting only a small collection of weddings each year, we are able to devote an uncommon level of attention to every celebration we document. We believe the best photographs come from trust, connection, and a genuine understanding of the people in front of the camera.
              </p>
              <p>
                The result is timeless imagery created with intention, care, and a commitment to preserving what matters most.
              </p>
            </div>

            {/* Core Values / 3 Bullets with custom icons */}
            <div className="space-y-4 pt-4">
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#1A1A1A]/5 flex items-center justify-center text-[#c4a482] shrink-0">
                  <Camera size={13} />
                </div>
                <div>
                  <h4 className="font-serif text-[16px] font-light font-medium text-[#1A1A1A]">Meticulous Attention</h4>
                  <p className="text-xs text-[#7f7d78] leading-relaxed mt-0.5 font-light">
                    Every reservation receives custom bespoke pre-planning, custom color tones, and complete artistic devotion to ensure each single moment is beautifully preserved.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#1A1A1A]/5 flex items-center justify-center text-[#c4a482] shrink-0">
                  <Feather size={13} />
                </div>
                <div>
                  <h4 className="font-serif text-[16px] font-light font-medium text-[#1A1A1A]">Bespoke Coordination</h4>
                  <p className="text-xs text-[#7f7d78] leading-relaxed mt-0.5 font-light">
                    We collaborate closely on styling palettes, lighting aesthetics, and visual narratives to achieve ultimate natural elegance during your celebration.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#1A1A1A]/5 flex items-center justify-center text-[#c4a482] shrink-0">
                  <Star size={13} />
                </div>
                <div>
                  <h4 className="font-serif text-[16px] font-light font-medium text-[#1A1A1A]">Exclusive Reservation Limit</h4>
                  <p className="text-xs text-[#7f7d78] leading-relaxed mt-0.5 font-light">
                    To deliver uninhibited, museum-grade craftsmanship and unmatched custom focus, we accept only a small, exclusive collection of wedding commissions worldwide each calendar year.
                  </p>
                </div>
              </div>

            </div>

            {/* CTA action wrapper optimized for touch targets (min 44px) */}
            <div className="pt-6 border-t border-[#4A1722]/10 mt-8 flex flex-col sm:flex-row gap-4 items-center">
              <button
                id="btn-about-join-waitlist"
                onClick={onJoinWaitlist}
                className="w-full sm:w-auto bg-[#4A1722] text-[#F8F5EF] hover:bg-[#241018] text-[10.5px] uppercase tracking-[0.25em] py-4 px-10 transition duration-500 cursor-pointer text-center font-display leading-none min-h-[44px] flex items-center justify-center font-semibold border-none"
              >
                JOIN THE WAITLIST
              </button>
              
              <p className="text-[10px] text-[#4A1722] uppercase tracking-[0.2em] font-semibold text-center sm:text-left">
                We take a limited number of weddings each year. Join our waitlist today.
              </p>
            </div>

          </div>

        </div>

      </div>
    </section>
  );
}
